// src/modules/wiring/hooks/useWireLocalUndoRedo.js
//
// H5 Aşama 3: useComponentWiring'den çıkarıldı.
// "LOCAL Wire Vertex Undo-Redo" — Ctrl+Z / Ctrl+Y çizim sırasında işliyor.
// Çizim bittikten sonra (Enter) global undo/redo manager devreye girer.
//
// Bu hook'un sorumluluğu:
// - Çizim aktifken Ctrl+Z: son vertex/socket/lightSwitch'i geri al (stack pop)
// - Ctrl+Y/Ctrl+Shift+Z: redo stack'i kullanarak vertex/component restore et
// - Wire marker'larını yeniden oluştur (her undo/redo sonrası)
// - Auto-finish modları (restoreVertices son vertex'e ulaşınca otomatik tamamla)

import { useEffect } from 'react';
import * as THREE from 'three';
import { resetOperation } from '../../../redux/operationSlice';
import { ComponentFactory } from '../../../factories/ComponentFactory';
import { FeederFactory } from '../../../factories/FeederFactory';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { singleCircleBuat, singleSquareBuat } from '../../../factory/symbols/createSocketSymbols';
import { WireMarkerGenerator } from '../utils/WireMarkerGenerator';
import { updatePanelExitPoints, updatePanelWiringPositions, calculatePanelExitCount } from '../utils/panelExitManager';

/**
 * @param {object} args
 * @param {THREE.Scene} args.scene
 * @param {object} args.base - useBaseWiring API
 * @param {object} args.options - Tüketici hook options (markerCount, commandType,
 *   skipItemExecution, onWireEnd, undoRedoManagerRef)
 * @param {() => void} args.confirmPlacement - Çizimi sonlandıran callback
 */
export function useWireLocalUndoRedo({ scene, base, options, confirmPlacement }) {
  useEffect(() => {
    if (!base.enabled() || !scene) return;

    const handleWireUndoRedo = (event) => {
      // ⚠️ SADECE ÇİZİM SIRASINDA ÇALIŞMALI (Enter sonrası global undo devreye girmeli)
      const isDrawingActive = base.startRef?.current === true;
      if (!isDrawingActive) return;

      // ── LOCAL UNDO (Ctrl+Z) ─────────────────────────────────────────────
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        if (base.undoStackRef.current.length === 0) {
          // ✅ Branch (ek) buat temizliği: re-draw, branch buat'ı (hattın ilk vertex'inde,
          // buatInfos'ta var ama hiçbir socket/lightSwitch action'ına bağlı DEĞİL — vi=0
          // restore loop'ta atlanıyor) buatInfos'tan yeniden yaratıyor; local undo onu
          // action olmadığı için silemiyordu. TAM undo'da buatInfos id'li tüm kalan
          // buat'ları sil (global undo zaten bunları siliyor; bu, re-draw'un yeniden
          // yarattıklarını süpürür). Redo SocketCommand/LightingCommand.execute ile
          // buatInfos'tan hepsini geri yaratır → güvenli.
          try {
            const od = base.operationData || {};
            const btIds = new Set((od.buatInfos || []).map(i => i?.id).filter(Boolean));
            if (btIds.size > 0) {
              const toRemove = [];
              scene.traverse(o => {
                const ud = o.userData; if (!ud) return;
                if ((ud.type === 'socketBuat' || ud.type === 'buatCircle' || ud.type === 'lightingBuat') && ud.id && btIds.has(ud.id)) {
                  toRemove.push(o);
                }
              });
              toRemove.forEach(o => {
                o.traverse?.(c => {
                  if (c.geometry) c.geometry.dispose();
                  if (Array.isArray(c.material)) c.material.forEach(m => m?.dispose?.());
                  else if (c.material) c.material.dispose();
                });
                if (o.parent) o.parent.remove(o);
              });
            }
          } catch (e) { /* cleanup */ }
          // ✅ fromLocalUndo=true: stack boşaldı → kullanıcı hattı TAM geri alıyor.
          // cancelWiring Esc-iptal re-execute'ini atlasın (yoksa hat yeniden yaratılıp
          // history'ye geri konuyordu = leftover + global undo loop).
          base.cancelWiring(true);
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const lastAction = base.undoStackRef.current.pop();

        // vertexCountBefore truncation: deterministik snapshot geri sarımı
        if (lastAction.vertexCountBefore !== undefined) {
          while (base.verticesRef.current.length > lastAction.vertexCountBefore) {
            base.verticesRef.current.pop();
          }
        } else {
          // Fallback: backward compat
          if (lastAction.type === 'vertex') {
            if (base.verticesRef.current.length > 0) base.verticesRef.current.pop();
          } else {
            const vertexCount = lastAction.verticesAdded || 1;
            for (let i = 0; i < vertexCount && base.verticesRef.current.length > 0; i++) {
              base.verticesRef.current.pop();
            }
          }
        }

        if (lastAction.type === 'socket' || lastAction.type === 'lightSwitch') {
          // skipRedux=true (Enter'da Redux'a eklenecek)
          lastAction.command.undo(scene, base.dispatch, true);

          if (lastAction.buatMesh) {
            // ✅ Parent-bağımsız sil: mesh-FP sweep buat'ı fpMesh'e re-parent etmiş
            // olabilir → scene.remove() onu bulamaz (kare buat / junction buat kalırdı).
            const bm = lastAction.buatMesh;
            bm.traverse?.(child => {
              if (child.geometry) child.geometry.dispose();
              if (child.material) {
                if (Array.isArray(child.material)) child.material.forEach(m => m?.dispose?.());
                else child.material.dispose();
              }
            });
            if (bm.geometry) bm.geometry.dispose();
            if (bm.material) bm.material.dispose();
            if (bm.parent) bm.parent.remove(bm);
            else scene.remove(bm);
          }

          // ✅ Fallback: buat'ı id ile de sahneden sil. buatMesh referansı mesh-FP
          // sweep'i (re-parent/clone) sonrası sahnedeki gerçek buat'tan kopabiliyor
          // → socket re-draw buat'ları local undo'da kalıyordu (buatInBuatInfos_reDraw).
          const _buatId = lastAction.buatInfo?.id;
          if (_buatId) {
            const _toRemove = [];
            scene.traverse(o => { if (o.userData?.id === _buatId) _toRemove.push(o); });
            _toRemove.forEach(o => {
              o.traverse?.(c => {
                if (c.geometry) c.geometry.dispose();
                if (Array.isArray(c.material)) c.material.forEach(m => m?.dispose?.());
                else if (c.material) c.material.dispose();
              });
              if (o.parent) o.parent.remove(o);
            });
          }

          if (base.itemsRef.current.length > 0) {
            base.itemsRef.current.pop();
            base.itemCountRef.current--;
          }
        }

        base.redoStackRef.current.push(lastAction);
        if (lastAction && lastAction.vertex) {
          base.redoStackRef.current[base.redoStackRef.current.length - 1].vertex = lastAction.vertex.clone();
        }

        base.updateTempLine();

        // Marker temizle + yeniden oluştur
        if (base.wireMarkersRef.current && base.wireMarkersRef.current.length > 0) {
          base.wireMarkersRef.current.forEach(marker => {
            if (marker) {
              scene.remove(marker);
              if (marker.geometry) marker.geometry.dispose();
              if (marker.material) marker.material.dispose();
            }
          });
          base.wireMarkersRef.current = [];
        }
        base.previewMarkersRef.current = [];

        WireMarkerGenerator.generateMarkersForSegments(
          base.verticesRef.current,
          base.previewMarkersRef.current,
          base.wireMarkersRef.current,
          scene,
          null,
          options.markerCount ?? 3,
          false,
          options.markerSkipFirstSegment === true
        );
      }

      // ── LOCAL REDO (Ctrl+Y / Ctrl+Shift+Z) ──────────────────────────────
      if (
        ((event.ctrlKey || event.metaKey) && event.key === 'y') ||
        ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'z')
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (base.redoStackRef.current.length === 0) return;

        const actionToRedo = base.redoStackRef.current.pop();

        if (actionToRedo.type === 'socket') {
          // Socket REDO
          const socketInfo = actionToRedo.socketInfo || actionToRedo.itemData;
          const p2 = actionToRedo.vertex.clone();
          const p1 = base.verticesRef.current[base.verticesRef.current.length - 1] || p2;
          const direction = new THREE.Vector3().subVectors(p2, p1);
          const angle = Math.atan2(direction.y, direction.x);

          try {
            const itemPower = socketInfo.power || 300;
            const componentId = socketInfo.id || generateUniqueId('socket');
            const sourcePanelId = base.snapSourceRef.current?.p?.panelID;

            const newComponent = ComponentFactory.createSocket(itemPower, {
              id: componentId,
              name: socketInfo.type,
              position: { x: p2.x, y: p2.y, z: 0 },
              rotation: { x: 0, y: 0, z: angle },
              electrical: { installedPower: itemPower, phaseCount: 1 }
            });

            const itemVertices = [p1, p2].map(vv => ({ x: vv.x, y: vv.y, z: vv.z }));
            const cableLength = p1.distanceTo ? Math.round(p1.distanceTo(p2)) : 0;
            const newFeeder = FeederFactory.createFeederForLoad('socket', itemPower, sourcePanelId, componentId, {
              cable: { length: Math.round(cableLength / 10), route: itemVertices },
              electrical: { phaseCount: 1, voltage: 220 },
              vertices: itemVertices
            });

            const itemCommand = base.createCommand('ADD', newComponent, newFeeder, {
              socketPosition: [p2.x, p2.y, 0],
              rotation: angle,
              dir: base.flipDirectionRef.current,
              color: base.wireColor,
              socketType: socketInfo.type,
              rotateValue: socketInfo.rotateValue || 0,
              wireVertices: itemVertices
            });

            itemCommand.execute(scene, base.dispatch, true);

            const vcbSocket = base.verticesRef.current.length;
            base.verticesRef.current.push(p2);
            base.itemsRef.current.push({ position: p2.clone(), type: socketInfo.type, rotateValue: socketInfo.rotateValue || 0, id: componentId });
            base.itemCountRef.current++;

            // Buat REDO
            let buatMesh = null;
            if (actionToRedo.buatInfo) {
              const buatInfo = actionToRedo.buatInfo;
              try {
                const buatPosition = { x: buatInfo.position.x, y: buatInfo.position.y };
                const buatColor = base.operationData?.wireColor ||
                  (buatInfo.function === 'simpleSocket' ? 0xff0000 : 0x0045ff);

                if (buatInfo.buatType === 'square') {
                  buatMesh = singleSquareBuat(scene, buatPosition, buatColor, buatInfo.id);
                } else {
                  buatMesh = singleCircleBuat(buatPosition, buatInfo.panelID, buatInfo.id, buatColor);
                }
                if (buatMesh) {
                  buatMesh.userData = {
                    ...buatMesh.userData,
                    id: buatInfo.id,
                    type: buatInfo.type || 'buat',
                    buatType: buatInfo.buatType || 'circle',
                    socketLoadId: componentId,
                    wireID: buatInfo.wireID,
                    panelID: buatInfo.panelID,
                    function: buatInfo.function
                  };
                  if (buatInfo.buatType !== 'square') {
                    scene.add(buatMesh);
                  }
                }
              } catch {
                // Socket Buat REDO failed
              }
            }

            base.undoStackRef.current.push({
              type: 'socket',
              vertex: p2,
              command: itemCommand,
              verticesAdded: 1,
              vertexCountBefore: vcbSocket,
              itemData: { position: p2.clone(), type: socketInfo.type, id: componentId },
              buatMesh: buatMesh,
              buatInfo: actionToRedo.buatInfo
            });
          } catch {
            // Fallback: sadece vertex
            const vcbFallback = base.verticesRef.current.length;
            base.verticesRef.current.push(p2);
            base.undoStackRef.current.push({ type: 'vertex', vertex: p2, vertexCountBefore: vcbFallback });
          }
        } else if (actionToRedo.type === 'lightSwitch') {
          // LightSwitch REDO (paralel socket logic)
          const lightSwitchInfo = actionToRedo.lightSwitchInfo || actionToRedo.itemData;
          const p2 = actionToRedo.vertex.clone();
          const p1 = base.verticesRef.current[base.verticesRef.current.length - 1] || p2;
          const direction = new THREE.Vector3().subVectors(p2, p1);
          const angle = Math.atan2(direction.y, direction.x);

          try {
            const itemPower = lightSwitchInfo.power || 300;
            const componentId = lightSwitchInfo.id || generateUniqueId('lightSwitch');
            const sourcePanelId = base.snapSourceRef.current?.p?.panelID;

            const newComponent = ComponentFactory.createLightSwitch(itemPower, {
              id: componentId,
              name: lightSwitchInfo.type,
              position: { x: p2.x, y: p2.y, z: 0 },
              rotation: { x: 0, y: 0, z: angle },
              electrical: { installedPower: itemPower, phaseCount: 1 }
            });

            const itemVertices = [p1, p2].map(vv => ({ x: vv.x, y: vv.y, z: vv.z }));
            const cableLength = p1.distanceTo ? Math.round(p1.distanceTo(p2)) : 0;
            const newFeeder = FeederFactory.createFeederForLoad('lighting', itemPower, sourcePanelId, componentId, {
              cable: { length: Math.round(cableLength / 10), route: itemVertices },
              electrical: { phaseCount: 1, voltage: 220 },
              vertices: itemVertices
            });

            const itemCommand = base.createCommand('ADD', newComponent, newFeeder, {
              lightSwitchPosition: [p2.x, p2.y, 0],
              rotation: angle,
              dir: base.flipDirectionRef.current,
              color: base.wireColor,
              lightSwitchType: lightSwitchInfo.type,
              rotateValue: lightSwitchInfo.rotateValue || 0,
              wireVertices: itemVertices
            });

            itemCommand.execute(scene, base.dispatch, true);

            const vcbLightSwitch = base.verticesRef.current.length;
            base.verticesRef.current.push(p2);
            base.itemsRef.current.push({ position: p2.clone(), type: lightSwitchInfo.type, rotateValue: lightSwitchInfo.rotateValue || 0, id: componentId });
            base.itemCountRef.current++;

            let buatMesh = null;
            if (actionToRedo.buatInfo) {
              const buatInfo = actionToRedo.buatInfo;
              try {
                const buatPosition = { x: buatInfo.position.x, y: buatInfo.position.y };
                const buatColor = base.operationData?.wireColor ||
                  (buatInfo.function === 'simpleSocket' ? 0xff0000 : 0x0045ff);

                if (buatInfo.buatType === 'square') {
                  buatMesh = singleSquareBuat(scene, buatPosition, buatColor, buatInfo.id);
                } else {
                  buatMesh = singleCircleBuat(buatPosition, buatInfo.panelID, buatInfo.id, buatColor);
                }
                if (buatMesh) {
                  buatMesh.userData = {
                    ...buatMesh.userData,
                    id: buatInfo.id,
                    type: buatInfo.type || 'buat',
                    buatType: buatInfo.buatType || 'circle',
                    socketLoadId: componentId,
                    wireID: buatInfo.wireID,
                    panelID: buatInfo.panelID,
                    function: buatInfo.function
                  };
                  if (buatInfo.buatType !== 'square') {
                    scene.add(buatMesh);
                  }
                }
              } catch {
                // LightSwitch Buat REDO failed
              }
            }

            base.undoStackRef.current.push({
              type: 'lightSwitch',
              vertex: p2,
              command: itemCommand,
              verticesAdded: 1,
              vertexCountBefore: vcbLightSwitch,
              itemData: { position: p2.clone(), type: lightSwitchInfo.type, id: componentId },
              buatMesh: buatMesh,
              buatInfo: actionToRedo.buatInfo
            });
          } catch {
            const vcbFallback = base.verticesRef.current.length;
            base.verticesRef.current.push(p2);
            base.undoStackRef.current.push({ type: 'vertex', vertex: p2, vertexCountBefore: vcbFallback });
          }
        } else if (actionToRedo.type === 'vertex') {
          const vcbVertex = base.verticesRef.current.length;
          base.verticesRef.current.push(actionToRedo.vertex.clone());
          base.undoStackRef.current.push({ ...actionToRedo, vertex: actionToRedo.vertex.clone(), vertexCountBefore: vcbVertex });
        }

        base.updateTempLine();

        // Marker temizle + yeniden oluştur
        if (base.wireMarkersRef.current && base.wireMarkersRef.current.length > 0) {
          base.wireMarkersRef.current.forEach(marker => {
            if (marker) {
              scene.remove(marker);
              if (marker.geometry) marker.geometry.dispose();
              if (marker.material) marker.material.dispose();
            }
          });
          base.wireMarkersRef.current = [];
        }
        base.previewMarkersRef.current = [];

        WireMarkerGenerator.generateMarkersForSegments(
          base.verticesRef.current,
          base.previewMarkersRef.current,
          base.wireMarkersRef.current,
          scene,
          null,
          options.markerCount ?? 3,
          false,
          options.markerSkipFirstSegment === true
        );

        // ── AUTO-FINISH: Local REDO sonrası transaction tamamla ─────────
        if (!base.operationData?.isRedo &&
            base.redoStackRef.current.length === 0 &&
            base.itemsRef.current.length > 0 &&
            base.commandType === options.commandType &&
            !options.skipItemExecution) {

          // Preview ve temp line'ları temizle
          if (base.previewSymbolRef.current) {
            scene.remove(base.previewSymbolRef.current);
            base.previewSymbolRef.current.traverse((child) => {
              if (child.geometry) child.geometry.dispose();
              if (child.material) child.material.dispose();
            });
            base.previewSymbolRef.current = null;
          }
          if (base.tempLineRef.current) {
            scene.remove(base.tempLineRef.current);
            base.tempLineRef.current.geometry.dispose();
            base.tempLineRef.current.material.dispose();
            base.tempLineRef.current = null;
          }

          base.autoFinishInProgressRef.current = true;
          base.startRef.current = false;

          setTimeout(() => {
            const undoRedoManager = options.undoRedoManagerRef?.current ||
              (typeof window !== 'undefined' ? window.undoRedoManager : null);

            if (undoRedoManager) {
              const nextTx = undoRedoManager.history[undoRedoManager.position + 1];
              if (nextTx) {
                nextTx.commands.forEach((cmd) => {
                  if (cmd?.execute) cmd.execute(scene, base.dispatch);
                });
                if (typeof undoRedoManager.notifyRedoComplete === 'function') {
                  undoRedoManager.notifyRedoComplete();
                }
                base.undoStackRef.current = [];
                base.redoStackRef.current = [];
                base.itemsRef.current = [];
                base.verticesRef.current = [];
                base.snapSourceRef.current = null;
                base.dispatch(resetOperation());
                base.autoFinishInProgressRef.current = false;
              } else {
                confirmPlacement();
                base.autoFinishInProgressRef.current = false;
              }
            } else {
              confirmPlacement();
              base.autoFinishInProgressRef.current = false;
            }
          }, 0);
          return;
        }

        // ── SOCKET/MACHINE/LIGHTING REDO: Son vertex eklenince orijinal command execute
        if (base.operationData?.isRedo &&
            base.redoStackRef.current.length === 0 &&
            base.commandType === options.commandType) {

          const undoRedoManager = options.undoRedoManagerRef?.current ||
            (typeof window !== 'undefined' ? window.undoRedoManager : null);

          if (undoRedoManager) {
            if (base.previewSymbolRef.current) {
              scene.remove(base.previewSymbolRef.current);
              base.previewSymbolRef.current.traverse((child) => {
                if (child.geometry) child.geometry.dispose();
                if (child.material) child.material.dispose();
              });
              base.previewSymbolRef.current = null;
            }
            if (base.tempLineRef.current) {
              scene.remove(base.tempLineRef.current);
              base.tempLineRef.current.geometry.dispose();
              base.tempLineRef.current.material.dispose();
              base.tempLineRef.current = null;
            }

            const nextTx = undoRedoManager.history[undoRedoManager.position + 1];
            if (nextTx && nextTx.commands) {
              nextTx.commands.forEach((cmd) => {
                if (cmd?.execute) {
                  cmd.execute(scene, base.dispatch);

                  try {
                    const feederData = cmd.data?.after?.feeder || cmd.data?.before?.feeder;
                    const componentData = cmd.data?.after?.component || cmd.data?.before?.component;
                    const bellLoadData = cmd.data?.after?.bellLoad || cmd.data?.before?.bellLoad;
                    const isBellCmd = cmd.type === 'ADD_BELL' || cmd.constructor?.name === 'BellCommand';
                    const isMachineCmd = cmd.type === 'ADD_MACHINE' || cmd.constructor?.name === 'MachineCommand';

                    if (isMachineCmd) return;

                    const cmdPanelId = feederData?.panelId || feederData?.sourceId ||
                      cmd.sceneData?.restoreSource?.panelID ||
                      cmd.sceneData?.restoreSource?.p?.panelID ||
                      bellLoadData?.parentComponentId;

                    if (cmdPanelId && scene) {
                      let cmdPanelObj = null;
                      scene.traverse((obj) => {
                        if (obj.userData?.id === cmdPanelId || obj.uuid === cmdPanelId) {
                          cmdPanelObj = obj;
                        }
                      });

                      if (cmdPanelObj) {
                        if (!cmdPanelObj.userData) cmdPanelObj.userData = {};
                        if (!cmdPanelObj.userData.outs) cmdPanelObj.userData.outs = [];

                        let exitData;
                        if (isBellCmd) {
                          const bellWireId = cmd.sceneData?.wireId || feederData?.id;
                          exitData = {
                            branchID: cmd.sceneData?.branchID,
                            power: bellLoadData?.power || 5,
                            branchType: 'bell',
                            description: bellLoadData?.name || 'Zil',
                            wireId: bellWireId,
                            loadId: bellLoadData?.id,
                            wires: [{
                              id: bellWireId,
                              power: bellLoadData?.power || 5
                            }],
                            addPosition: 'start',
                            source: cmd.sceneData?.restoreSource || null,
                            metadata: {
                              wireId: bellWireId,
                              feederId: feederData?.id,
                              isRedo: true
                            }
                          };
                        } else {
                          const actualWireId = feederData?.wireID || cmd.sceneData?.wireID || cmd.sceneData?.wireId || feederData?.id;
                          exitData = {
                            loadId: componentData?.id,
                            wireId: actualWireId,
                            addPosition: 'start',
                            source: cmd.sceneData?.restoreSource || null,
                            metadata: {
                              wireID: cmd.sceneData?.wireID || cmd.sceneData?.wireId || feederData?.wireID,
                              feederId: feederData?.id,
                              isRedo: true
                            }
                          };
                        }

                        const alreadyExists = cmdPanelObj.userData.outs.some(
                          out => out.wireId === exitData.wireId ||
                            out.loadId === exitData.loadId ||
                            (isBellCmd && out.branchID === exitData.branchID)
                        );

                        if (!alreadyExists) {
                          cmdPanelObj.userData.outs.push(exitData);
                        }
                      }
                    }
                  } catch {
                    // outs update error
                  }
                }
              });

              // Panel çıkış noktalarını güncelle
              try {
                const source = base.operationData?.source || base.snapSourceRef.current;
                if (source?.type === 'panelConnection' || source?.p?.panelID || source?.panelID) {
                  const panelUUID = source.panelID || source.UUID ||
                    source.p?.UUID || source.p?.id || source.p?.panelID || source.no;

                  if (panelUUID && scene) {
                    let panelObj = null;
                    scene.traverse((obj) => {
                      if (obj.uuid === panelUUID || obj.userData?.id === panelUUID) {
                        panelObj = obj;
                      }
                    });
                    if (panelObj) {
                      const exitCount = Array.isArray(panelObj.userData?.outs)
                        ? panelObj.userData.outs.length
                        : calculatePanelExitCount(panelObj);
                      if (exitCount > 0) {
                        updatePanelExitPoints(panelObj, exitCount, scene);
                        updatePanelWiringPositions(scene, panelObj);
                      }
                    }
                  }
                }
              } catch {
                // Panel update error
              }
            }

            base.undoStackRef.current = [];
            base.redoStackRef.current = [];
            base.itemsRef.current = [];
            base.verticesRef.current = [];
            base.snapSourceRef.current = null;
            base.startRef.current = false;
            base.dispatch(resetOperation());

            setTimeout(() => {
              if (typeof undoRedoManager.notifyRedoComplete === 'function') {
                undoRedoManager.notifyRedoComplete();
              }
            }, 50);
          }
          return;
        }

        // ── AUTO-FINISH: restoreVertices son vertex'e ulaşınca tamamla ──
        const restoreVertices = base.operationData?.restoreVertices;
        const skipItemExecution = options.skipItemExecution;
        const isRedoMode = base.operationData?.isRedo;

        if (restoreVertices && restoreVertices.length > 0 && skipItemExecution && !isRedoMode &&
            base.commandType === options.commandType) {
          const currentLength = base.verticesRef.current.length;
          const restoreLength = restoreVertices.length;

          if (currentLength === restoreLength && base.itemsRef.current.length === 0) {
            const currentLast = base.verticesRef.current[currentLength - 1];
            const restoreLast = new THREE.Vector3(restoreVertices[restoreLength - 1].x, restoreVertices[restoreLength - 1].y, restoreVertices[restoreLength - 1].z);
            const distance = currentLast.distanceTo(restoreLast);

            if (distance < 0.01) {
              base.autoFinishInProgressRef.current = true;
              base.startRef.current = false;

              if (base.previewSymbolRef.current) {
                scene.remove(base.previewSymbolRef.current);
                base.previewSymbolRef.current.traverse((child) => {
                  if (child.geometry) child.geometry.dispose();
                  if (child.material) child.material.dispose();
                });
                base.previewSymbolRef.current = null;
              }
              if (base.tempLineRef.current) {
                scene.remove(base.tempLineRef.current);
                base.tempLineRef.current.geometry.dispose();
                base.tempLineRef.current.material.dispose();
                base.tempLineRef.current = null;
              }

              setTimeout(() => {
                const undoRedoManager = options.undoRedoManagerRef?.current ||
                  (typeof window !== 'undefined' ? window.undoRedoManager : null);

                const clearState = () => {
                  base.undoStackRef.current = [];
                  base.redoStackRef.current = [];
                  base.itemsRef.current = [];
                  base.verticesRef.current = [];
                  base.snapSourceRef.current = null;
                  base.dispatch(resetOperation());
                  base.autoFinishInProgressRef.current = false;
                };

                const fallbackOnWireEnd = () => {
                  if (options.onWireEnd && typeof options.onWireEnd === 'function') {
                    const source = base.snapSourceRef.current || base.operationData?.source;
                    options.onWireEnd(
                      base.verticesRef.current,
                      base.itemsRef.current,
                      source,
                      null,
                      base.tempLineRef.current?.uuid,
                      base.wireMarkersRef.current
                    );
                  }
                  clearState();
                };

                if (undoRedoManager) {
                  const nextTx = undoRedoManager.history[undoRedoManager.position + 1];
                  if (nextTx) {
                    nextTx.commands.forEach((cmd) => {
                      if (cmd?.execute) cmd.execute(scene, base.dispatch);
                    });
                    if (typeof undoRedoManager.notifyRedoComplete === 'function') {
                      undoRedoManager.notifyRedoComplete();
                    } else {
                      undoRedoManager.position = Math.min(
                        undoRedoManager.history.length - 1,
                        undoRedoManager.position + 1
                      );
                    }
                    clearState();
                  } else {
                    fallbackOnWireEnd();
                  }
                } else {
                  fallbackOnWireEnd();
                }
              }, 0);
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleWireUndoRedo, { capture: true });
    return () => window.removeEventListener('keydown', handleWireUndoRedo, { capture: true });
  }, [base.commandType, scene, options.skipItemExecution, options.onWireEnd, options.undoRedoManagerRef, base, options, confirmPlacement]);
}

export default useWireLocalUndoRedo;
